import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AccountCoordinatorAssignment } from 'src/app/accountmanagers/am-to-ac-pairing/models/account-coordinator-assignment.model';
import { AccountCoordinatorAssignmentsGridResponse } from 'src/app/accountmanagers/am-to-ac-pairing/models/account-coordinator-assignments-grid-response.model';
import {
    CandidateBulkMovedData,
    MatchedCandidateData,
    NewJobsBulkMoved
} from 'src/app/accountmanagers/manage-candidates-bulk/models/manage-candidate-bulk.model';
import { InfoHospitalMarker } from 'src/app/accountmanagers/models/info-hospital-marker.model';
import {
    JobFulfillmentCandidatePool,
    JobFulfillmentMyAyaActivity,
    JobFulfillmentRecentPlacements,
    JobFulfillmentTimeToFillRange
} from 'src/app/accountmanagers/shared/models/job-fulfillment-analysis.model';
import { CandidateToMoveData } from 'src/app/accountmanagers/shared/models/move-candidates-job.model';
import { CurrentTravelersPlacedInWFDJobDto } from 'src/app/accountmanagers/workforce-disruption-placements/models/current-travelers-placed-in-wfd-job-dto';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';
import { AccountManagersPayRate } from 'src/app/shared/models/account-managers-pay-rate.model';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { IdNameModel } from 'src/app/shared/models/id-name.model';
import { CountryState } from 'src/app/shared/models/lookup-country-states.model';
import { JobApprovalStatus } from 'src/app/shared/models/lookup-job-approval-status.model';
import { JobOpenReason } from 'src/app/shared/models/lookup-job-open-reason.model';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { generateQuery } from 'src/app/shared/utilities/grid-query-params';
import { appendQuery, arrayToQuery, combine, getQueryString } from 'src/app/shared/utilities/route';
import mockCountryStates from 'src/mock/mock-country-states';
import mockDates from 'src/mock/mock-dates';
import mockLocations from 'src/mock/mock-locations';
import mockProfessions from 'src/mock/mock-professions';
import mockShifts from 'src/mock/mock-shifts';
import mockSpecialties from 'src/mock/mock-specialties';
import { CandidatesService } from '../shared/lib/common/candidates.service';
import { ClientJobUpdateOpenPositionsCountRequest } from '../shared/models/account-managers-edit-job-open-positions.model';
import { JobGridLookups } from '../shared/models/job-grid-lookups';
import { SimpleIdName } from '../shared/models/simple-id-name.model';
import { JobExtensionRequest } from './candidates/model/job-extension-request.model';
import { JobExtension } from './candidates/model/job-extension.model';
import { HospitalJobModel } from './jobs-import/shared/model/hospital-job.model';
import { HospitalMapFullDeleteModel } from './jobs-import/shared/model/hospital-map-full-delete.model';
import { HospitalMapFullSaveModel } from './jobs-import/shared/model/hospital-map-full-save.model';
import { HospitalMapFullModel } from './jobs-import/shared/model/hospital-map-full.model';
import { HospitalMatchesModel } from './jobs-import/shared/model/hospital-matches.model';
import { ProfessionExpertiseMapSaveModel } from './jobs-import/shared/model/profession-expertise-map-save.model';
import { ProfessionExpertiseMapModel } from './jobs-import/shared/model/profession-expertise-map.model';
import { SelfApplyApplicationConfigurationModel } from './jobs/dialog/edit-selfapply-application-type/selfapply-application-configuration-model';
import { SampleMarginJobsLinkModel } from './jobs/dialog/link-sample-margin-to-jobs/link-sample-margin-to-jobs-model';
import { ClinicianJobMatch } from './jobs/dialog/match-jobs-to-candidate/clinicianJobMatch.model';
import { ContractedCandidateToMoveToJobDto } from './shared/models/contracted-candidate-to-move-to-job-dto';
import { AmSubmittalsReassign } from './submittals/model/am-submittals-reassign.model';
import { SubmittalTypeEnum } from './submittals/model/am-submittals.enum';
import {
    AMNoteItem,
    AMSubmittalJob,
    SubmittalData,
    SubmittalDataSummary,
    SubmittalInstructionsModel,
    SubmittalWithOfferNote,
    SubmittalClientNote,
    ReplyToSubmittalNote,
    SubmittalActivityNotes
} from './submittals/model/am-submittals.model';
import { WocDetailsItem } from './work-order-confirmations/models/woc-details-item.model';
import { WocGridResponse } from './work-order-confirmations/models/woc-grid-response.model';
import { WocStatusChange } from './work-order-confirmations/models/woc-status-change.model';
import { WocUploadData } from './work-order-confirmations/models/woc-upload-data.model';
import { ReleaseGroup } from '../shared/models/release-group.model';
import { JobPositionReleaseHistory } from '../shared/models/job-position-release-hisory';
import { BulkUpdateJobsResult } from './models/bulk-update-jobs-result.model';
import { SubmittalPreferences } from './submittals/am-submittal-status-update/send-to-client-modal/advanced-send-to-client-portal/models/submittal-preferences.model';
import { FacilityDataDto } from './models/facility-data-dto.model';

@Injectable({
    providedIn: 'root'
})
export class AccountManagersService implements OnDestroy {
    private readonly _v1Domain: string;
    private readonly _coreApiPath: string;

    private readonly _submittalChanged: EventEmitter<{ submittalId: number }> = new EventEmitter<{
        submittalId: number;
    }>();

    get submittalChanged(): Observable<{ submittalId: number }> {
        return this._submittalChanged.asObservable();
    }

    constructor(
        private readonly _httpClient: HttpClient,
        private readonly _candidatesService: CandidatesService,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'AyaNova');
        this._v1Domain = this._settings.v1Domain;
    }

    ngOnDestroy(): void {
        this._submittalChanged.complete();
    }

    getJobs(args: FetchArgs): Observable<any> {
        const { pageSize, skip } = args;
        let url = `${this._coreApiPath}/job/account-manager-jobs`;

        if (args.filter) {
            url += this.getQueryString(args.filter);
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this._httpClient.get<any>(path, { observe: 'response' }).pipe(
            map((response) => {
                const totalCount = response.body.totalCount;
                const totalFiltered = response.body.totalFilteredCount;
                const totalOpenPositionsCount = response.body.totalOpenPositions;
                const totalFilteredOpenPositionsCount = response.body.totalFilteredOpenPositions;

                return {
                    data: response.body.data,
                    total: totalCount, // Needs to be this for grid paging (visible rows)
                    totalFiltered: totalFiltered,
                    totalOpenPositions: totalOpenPositionsCount,
                    totalFilteredOpenPositions: totalFilteredOpenPositionsCount
                };
            })
        );
    }

    getJobsNew(args: FetchArgs): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-jobs/new`;
        const params = generateQuery(args);
        return this._httpClient.get<any>(url, { params }).pipe(
            map((response) => {
                return {
                    data: response.data,
                    total: response.totalCount,
                    totalFiltered: response.totalFilteredCount,
                    totalOpenPositions: response.totalOpenPositions,
                    totalFilteredOpenPositions: response.totalFilteredOpenPositions,
                    forExport: args.filter.forExport
                };
            })
        );
    }

    addMockData(items: any[]) {
        const newItems = [];
        if (items) {
            items.forEach((item, index) => {
                item.contractStartDate = mockDates[index % mockDates.length];
                const mockShift = mockShifts[index % mockShifts.length];
                const mockProfession = mockProfessions[index % mockProfessions.length];
                const profession = {
                    professionId: mockProfession.id,
                    professionName: mockProfession.name,
                    professionNameAbbreviated: mockProfession.abbreviation
                };
                const mockSpecialty = mockSpecialties[index % mockSpecialties.length];
                const specialty = {
                    specialtyId: mockSpecialty.id,
                    specialtyName: mockSpecialty.name
                };
                const mockCountryState = mockCountryStates[index % mockCountryStates.length];
                const countryState = {
                    stateCode: mockCountryState.code,
                    stateId: mockCountryState.id,
                    stateName: mockCountryState.name
                };
                const mockLocation = mockLocations[index % mockLocations.length];
                const location = {
                    city: mockLocation.city,
                    latitude: mockLocation.latitude,
                    longitude: mockLocation.longitude
                };
                const newItem = {
                    ...item,
                    ...mockShift,
                    ...profession,
                    ...specialty,
                    ...countryState,
                    ...location,
                    billRate: `\$${index}.00`,
                    daysAged: `${index * 2}`,
                    openPositions: index,
                    totalPositions: index + 1,
                    ayaSubs: index * 3,
                    apnSubs: index * 4
                };
                newItems.push(newItem);
            });
        }

        return newItems;
    }

    getCountryStates(): Observable<CountryState[]> {
        const url = `${this._coreApiPath}/accountmanager/lookups/country-states`;
        return this._httpClient.get<CountryState[]>(url);
    }

    getJobPositions(jobId: number): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-job-positions?jobId=${jobId}`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getJobScheduledPositions(jobId: number): Observable<any> {
        const url = `${this._coreApiPath}/account-manager/job-position-release/${jobId}/scheduled-positions-releases`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getReleaseGroupsByFacility(facilityId: number): Observable<ReleaseGroup[]> {
        const url = `${this._coreApiPath}/account-manager/job-position-release/${facilityId}/release-groups`;
        return this._httpClient.get<ReleaseGroup[]>(url);
    }

    releaseAllJobPositions(jobId: number, vendorIds: number[]) {
        const url = `${this._coreApiPath}/account-manager/job-position-release/${jobId}/manual-release-positions`;
        return this._httpClient.put(url, vendorIds);
    }

    releaseJobPosition(jobId: number, jobPositionId: number, vendorIds: number[]) {
        const url = `${this._coreApiPath}/account-manager/job-position-release/${jobId}/positions/${jobPositionId}/manual-release-position`;
        return this._httpClient.put(url, vendorIds);
    }

    getJobPositionReleaseHistory(positionId: number): Observable<JobPositionReleaseHistory[]> {
        const url = `${this._coreApiPath}/account-manager/job-position-release/${positionId}/release-history`;
        return this._httpClient.get<JobPositionReleaseHistory[]>(url);
    }

    getJobSubmittals(jobId: number): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-job-submittals?jobId=${jobId}`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getJobRemovalValidReasons(): Observable<any> {
        const url = `${this._coreApiPath}/job/job-removal-valid-reasons`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getJobOpenReasons(): Observable<JobOpenReason[]> {
        const url = `${this._coreApiPath}/lookup/job-open-reasons`;
        return this._httpClient.get<JobOpenReason[]>(url);
    }

    getJobApprovalStatuses(): Observable<JobApprovalStatus[]> {
        const url = `${this._coreApiPath}/job/lookup/job-approval-statuses`;
        return this._httpClient.get<JobApprovalStatus[]>(url);
    }

    getIsFacilityFeatureEnabled(facilityId: number, featureId: number): Observable<boolean> {
        const url = `${this._coreApiPath}/facility/${facilityId}/features/${featureId}`;
        return this._httpClient.get<boolean>(url);
    }

    removeJob(jobId: number, closeReasonId: number) {
        let url = `${this._coreApiPath}/job/account-manager-job/${jobId}`;
        if (closeReasonId) {
            url += `?closeReasonId=${closeReasonId}`;
        }
        return this._httpClient.delete<any>(url, { observe: 'response' });
    }

    putUpdateJobPositions(data: any): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-job-positions/`;
        return this._httpClient.put<any>(url, data, { observe: 'response' });
    }

    getPositionCloseReasons() {
        const options = [
            { id: 0, value: 'N/A' },
            { id: 1, value: 'Filled' },
            { id: 2, value: 'Client Request' },
            { id: 3, value: 'Other' },
            { id: 4, value: 'Client Found Staff Candidate' },
            { id: 5, value: 'Client Budget Changed' },
            { id: 6, value: 'Client Census Dropped' },
            { id: 7, value: 'Another Agency Filled Position' }
        ];

        return options;
    }

    getSubmittalInstructions(
        submittalType: SubmittalTypeEnum,
        submittalIds: number[]
    ): Observable<SubmittalInstructionsModel[]> {
        if (submittalIds && submittalIds.length > 0) {
            const url = `${this._coreApiPath}/accountmanager/submittals/${submittalType}/instructions`;
            return this._httpClient.post<SubmittalInstructionsModel[]>(url, submittalIds);
        } else {
            return of([]);
        }
    }

    getPositionCloseReasonsForSelect() {
        const options = [
            { id: 7, value: 'Another Agency Filled Position' },
            { id: 5, value: 'Client Budget Changed' },
            { id: 6, value: 'Client Census Dropped' },
            { id: 4, value: 'Client Found Staff Candidate' },
            { id: 2, value: 'Client Request' },
            { id: 3, value: 'Other' }
        ];

        return options;
    }

    getPercentForSelect(): number[] {
        const options = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
        return options;
    }

    getUnitFormUrl(userId: number, unitFormId: string, isLotusOneUrlReference: boolean): string | null {
        return this._candidatesService.getUnitFormUrl(userId, unitFormId, isLotusOneUrlReference);
    }

    getUnitTypeNamesForGrid(unitTypes: SimpleIdName[], forTooltip = false, forExport = false): string {
        return this._candidatesService.getUnitTypeNamesForGrid(unitTypes, forTooltip, forExport);
    }

    getV1ProfileUrl(itemId: string, travalerType: string): string {
        let v1Domain = this._v1Domain;
        if (v1Domain.indexOf('http') <= 0) {
            v1Domain = '//' + v1Domain;
        }

        if (!v1Domain.endsWith('/')) {
            v1Domain = v1Domain + '/';
        }

        if (travalerType === 'nurse') {
            return `${v1Domain}/employers/employersNursesProfileView.asp?navItem=employers&userID=${itemId}`;
        } else if (travalerType === 'subcontractor') {
            return `${v1Domain}/employers/subcontractorProfile.asp?userID=${itemId}`;
        } else if (travalerType === 'contract') {
            return `${v1Domain}/employers/employersTrackRNContractView.asp?contractID=${itemId}`;
        }

        return undefined;
    }

    postAddJobPositions(data: ClientJobUpdateOpenPositionsCountRequest): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-job-positions/`;
        return this._httpClient.post<any>(url, data, { observe: 'response' });
    }

    putCloseJobPositions(data: ClientJobUpdateOpenPositionsCountRequest): Observable<any> {
        const url = `${this._coreApiPath}/job/account-manager-job-positions/${data.jobId}`;
        return this._httpClient.put<any>(url, data, { observe: 'response' });
    }

    getJobDetails(jobId: number): Observable<any> {
        const url = `${this._coreApiPath}/Job/account-manager-jobs/detail/${jobId}`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getJobFulfillmentTimeToFillRange(jobId: number): Observable<JobFulfillmentTimeToFillRange> {
        return this._httpClient.get<JobFulfillmentTimeToFillRange>(
            `${this._coreApiPath}/JobFulfillmentAnalysis/${jobId}/time-to-fill-range`
        );
    }

    getJobFulfillmentCandidatePool(jobId: number, location: string): Observable<JobFulfillmentCandidatePool> {
        const url =
            `${this._coreApiPath}/JobFulfillmentAnalysis/${jobId}/candidatepool` +
            (location ? `?location=${location}` : '');
        return this._httpClient.get<JobFulfillmentCandidatePool>(url);
    }

    getJobFulfillmentRecentPlacements(jobId: number, location: string): Observable<JobFulfillmentRecentPlacements> {
        const url =
            `${this._coreApiPath}/JobFulfillmentAnalysis/${jobId}/recentplacements` +
            (location ? `?location=${location}` : '');
        return this._httpClient.get<JobFulfillmentRecentPlacements>(url);
    }

    getJobFulfillmentMyAyaActivity(jobId: number): Observable<JobFulfillmentMyAyaActivity> {
        const url = `${this._coreApiPath}/JobFulfillmentAnalysis/${jobId}/myayaactivity`;
        return this._httpClient.get<JobFulfillmentMyAyaActivity>(url);
    }

    getPayRate(jobId: number): Observable<AccountManagersPayRate> {
        const url = `${this._coreApiPath}/AccountManager/Job/${jobId}/perdiem-payrates`;
        return this._httpClient.get<AccountManagersPayRate>(url);
    }

    putEditStartDate(startDate: string, jobsIds: number[]): Observable<any> {
        const data = {
            startDate: startDate,
            jobsIds: jobsIds
        };
        const url = `${this._coreApiPath}/Job/account-manager-jobs/start-date`;
        return this._httpClient.put<any>(url, data, { observe: 'response' });
    }

    getJobAddValidOptions(): Observable<HttpResponse<any>> {
        const url = `${this._coreApiPath}/Jobs/lookups`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getValidFacilities(searchString: string): Observable<HttpResponse<InfoHospitalMarker[]>> {
        const url = `${this._coreApiPath}/Hospital/markers`;
        const params = { filter: searchString };
        return this._httpClient.get<InfoHospitalMarker[]>(url, { observe: 'response', params });
    }

    getFacilitiesData(searchString: string): Observable<FacilityDataDto[]> {
        const url = `${this._coreApiPath}/Hospital/facility-data`;
        const params = { filter: searchString };
        return this._httpClient
            .get<FacilityDataDto[]>(url, { observe: 'response', params })
            .pipe(map((results) => results.body));
    }

    getFacilityData(id: number): Observable<FacilityDataDto> {
        const url = `${this._coreApiPath}/Hospital/facility-data/${id}`;
        return this._httpClient.get<FacilityDataDto>(url);
    }

    addNewJob(data: any): Observable<HttpResponse<any>> {
        const url = `${this._coreApiPath}/accountmanager/jobs`;
        return this._httpClient.post<any>(url, data, { observe: 'response' });
    }

    saveJob(data: any): Observable<HttpResponse<any>> {
        const url = `${this._coreApiPath}/accountmanager/jobs`;
        return this._httpClient.put<any>(url, data, { observe: 'response' });
    }

    getValidUnits(facilityId: number) {
        const url = `${this._coreApiPath}/Hospital/${facilityId}/units`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    getUnitDescriptionFormId(unitId: number): Observable<string> {
        const url = `${this._settings.coreAPIUrl}/AyaConnect/Unit/${unitId}/description-form-id`;
        return this._httpClient.get<string>(url);
    }

    getStateAndCity(facilityId: number): Observable<any> {
        const url = `${this._coreApiPath}/ClientServices/HospClientOverview?hospId=${facilityId}`;
        return this._httpClient.get<any>(url, { observe: 'response' });
    }

    putAdvertisedJobs(data: any): Observable<any> {
        const url = `${this._coreApiPath}/accountmanager/jobs/advertise`;
        return this._httpClient.put<any>(url, data, { observe: 'response' });
    }

    getSubmittals(userId: number, args: any): Observable<PagingToken<SubmittalData[]>> {
        const url = `${this._coreApiPath}/accountmanager/${userId}/submittals`;
        return this.getGridPaged<SubmittalData>(args.pageSize, args.skip, args.filter, args.sort, url);
    }

    getJobImporterMappingCategories(): Observable<Lookup[]> {
        const url = `${this._coreApiPath}/jobs/import/jobs-importer-mapping-categories`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getProfessions(): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/jobs/import/professions`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url);
    }

    getProfessionExpertises(data: number): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/jobs/import/profession-expertises`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url, {
            params: { professionCode: data.toString() }
        });
    }

    getHospitalMatches(args: FetchArgs): Observable<PagingToken<HospitalMatchesModel[]>> {
        const url = `${this._coreApiPath}/jobs/import/hospital-matches${this.getQueryString(args.filter)}`;
        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this._httpClient.get<PagingToken<HospitalMatchesModel[]>>(path);
    }

    getProfessionExpertiseMapping(args: FetchArgs): Observable<PagingToken<ProfessionExpertiseMapModel[]>> {
        const url = `${this._coreApiPath}/jobs/import/profession-expertise-mapping`;
        return this._httpClient.post<PagingToken<ProfessionExpertiseMapModel[]>>(url, args);
    }

    getHospitalMapping(args: FetchArgs): Observable<PagingToken<HospitalMapFullModel[]>> {
        const url = `${this._coreApiPath}/jobs/import/hospital-mapping`;
        return this._httpClient.post<PagingToken<HospitalMapFullModel[]>>(url, args);
    }

    deleteProfessionExpertiseMapping(ids: number[]): Observable<Response> {
        const url = `${this._coreApiPath}/jobs/import/profession-expertise-mapping`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: ids
        };
        return this._httpClient.delete<Response>(url, options);
    }

    deleteHospitalMapping(maps: HospitalMapFullDeleteModel[]): Observable<Response> {
        const url = `${this._coreApiPath}/jobs/import/hospital-mapping`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: maps
        };
        return this._httpClient.delete<Response>(url, options);
    }

    saveProfessionExpertiseMapping(data: ProfessionExpertiseMapSaveModel): Observable<Response> {
        const url = `${this._coreApiPath}/jobs/import/save-profession-expertise-mapping`;
        return this._httpClient.post<Response>(url, data);
    }

    saveHospitalMapping(data: HospitalMapFullSaveModel): Observable<Response> {
        const url = `${this._coreApiPath}/jobs/import/save-hospital-mapping`;
        return this._httpClient.post<Response>(url, data);
    }

    previewJobs(file: File, isPermanentPlacement = false): Observable<HospitalJobModel[]> {
        const data = new FormData();

        data.append('file', file, file.name);

        if (isPermanentPlacement) {
            data.append('isPermanentPlacement', `${isPermanentPlacement}`);
        }
        const url = `${this._coreApiPath}/jobs/import/parse-excel-data`;
        return this._httpClient.post<HospitalJobModel[]>(url, data);
    }

    addJobs(file: File, isPermanentPlacement = false): Observable<HospitalJobModel[]> {
        const url = `${this._coreApiPath}/jobs/import`;
        const data = new FormData();

        data.append('file', file, file.name);

        if (isPermanentPlacement) {
            data.append('isPermanentPlacement', `${isPermanentPlacement}`);
        }
        return this._httpClient.post<HospitalJobModel[]>(url, data);
    }

    updateJobs(file: File, updateColumnsList: string[], isPermanentPlacement = false): Observable<HospitalJobModel[]> {
        const url = `${this._coreApiPath}/jobs/import`;
        const data = new FormData();

        data.append('file', file, file.name);
        data.append('updateColumnsListJson', JSON.stringify(updateColumnsList));

        if (isPermanentPlacement) {
            data.append('isPermanentPlacement', `${isPermanentPlacement}`);
        }
        return this._httpClient.put<HospitalJobModel[]>(url, data);
    }

    deleteJobs(file: File): Observable<HospitalJobModel[]> {
        const data = new FormData();

        data.append('file', file, file.name);

        const url = `${this._coreApiPath}/jobs/import`;
        const options = {
            headers: new HttpHeaders(),
            body: data
        };
        return this._httpClient.delete<HospitalJobModel[]>(url, options);
    }

    getSubmittalNotes(submittalId: number): Observable<AMNoteItem[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submittalId}/notes`;
        return this._httpClient.get<AMNoteItem[]>(url);
    }

    getSubmittalWithOfferLastNotes(submittals: SubmittalDataSummary[]): Observable<SubmittalWithOfferNote[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/lastnotes`;
        return this._httpClient.post<SubmittalWithOfferNote[]>(url, submittals);
    }

    getSubmittalActivityNotes(submitIds: number[]): Observable<SubmittalActivityNotes[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/activities`;
        return this._httpClient.post<SubmittalActivityNotes[]>(url, submitIds);
    }

    getSubmittalClientNotes(submittalIds: number[]): Observable<SubmittalClientNote[]> {
        if (submittalIds === undefined || submittalIds.length === 0) {
            return of([]);
        }
        const url = `${this._coreApiPath}/accountmanager/submittals/clientnotes`;
        return this._httpClient.post<SubmittalClientNote[]>(url, submittalIds);
    }

    triggerSubmittalChanged(submittalId: number) {
        this._submittalChanged.next({ submittalId: submittalId });
    }

    addNote(note: AMNoteItem, type: SubmittalTypeEnum): Observable<AMNoteItem> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${note.submitId}/notes?submittalType=${type}`;
        return this._httpClient
            .post<AMNoteItem>(url, note)
            .pipe(tap(() => this.triggerSubmittalChanged(note.submitId)));
    }

    getSubmittalJobs(submittalId: number, type: SubmittalTypeEnum): Observable<AMSubmittalJob[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submittalId}/jobs?submittalType=${type}`;
        return this._httpClient.get<AMSubmittalJob[]>(url);
    }

    getSubmittalsJobs(submittalIds: number[], type: SubmittalTypeEnum): Observable<AMSubmittalJob[]> {
        if (!submittalIds || submittalIds.length === 0) {
            return of<AMSubmittalJob[]>([]);
        }

        return this._httpClient.post<AMSubmittalJob[]>(
            `${this._coreApiPath}/accountmanager/submittals/${type}/jobs`,
            submittalIds
        );
    }

    markNotesAsRead(submitId: number): Observable<void> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submitId}/notes/mark-as-read`;
        return this._httpClient.put<void>(url, {});
    }

    markNotesAsReadByIdAndType(submitId: number, submittalType: SubmittalTypeEnum): Observable<void> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submitId}/${submittalType}/notes/mark-as-read-ll-notes`;
        return this._httpClient.put<void>(url, {});
    }

    getSubmittalJobHistory(
        candidateId: number,
        type: SubmittalTypeEnum,
        args: FetchArgs
    ): Observable<AMSubmittalJob[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${type}/submittal-history/${candidateId}`;
        return this._httpClient.post<AMSubmittalJob[]>(url, args);
    }

    getAllAccountManagers(): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/AccountManager`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url);
    }

    getAccountManagers(): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments/account-managers`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url);
    }

    getAccountManagerSubordinates(): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/AccountManager/subordinates`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url);
    }

    getAccountCoordinators(): Observable<Array<IdNameModel<number, string>>> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments/account-coordinators`;
        return this._httpClient.get<Array<IdNameModel<number, string>>>(url);
    }

    getAccountCoordinatorAssignments(args: FetchArgs): Observable<AccountCoordinatorAssignmentsGridResponse> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments`;
        const query = AccountManagersService.generateUrl(args, url);

        return this._httpClient.get<AccountCoordinatorAssignmentsGridResponse>(query, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        });
    }

    addAssignment(assignment: AccountCoordinatorAssignment): Observable<Response> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments`;
        return this._httpClient.post<Response>(url, assignment);
    }

    updateAssignment(assignment: AccountCoordinatorAssignment): Observable<Response> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments`;
        return this._httpClient.put<Response>(url, assignment);
    }

    deleteAssignment(assignment: AccountCoordinatorAssignment): Observable<Response> {
        const url = `${this._coreApiPath}/accountcoordinatorassignments`;
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: assignment
        };
        return this._httpClient.delete<Response>(url, options);
    }

    getFilteredWocs(args: any): Observable<WocGridResponse> {
        let url = `${this._coreApiPath}/AccountManager/woc-filtered`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort?.field?.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this._httpClient.get<WocGridResponse>(path);
    }

    getFilteredWocsForAccountManager(args: any, accountManagerId: number): Observable<WocGridResponse> {
        let url = `${this._coreApiPath}/AccountManager/woc-filtered`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort?.field?.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        if (accountManagerId > 0) {
            url = appendQuery(url, `accountManagerId=${accountManagerId}`);
        }

        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this._httpClient.get<WocGridResponse>(path);
    }

    getWocDetails(contractId: number): Observable<WocDetailsItem[]> {
        const url = `${this._coreApiPath}/woc-queue/${contractId}/details`;

        return this._httpClient.get<WocDetailsItem[]>(url);
    }

    lookupWocModifiedBy(accountManagerId?: number): Observable<OptionModel[]> {
        let url = `${this._coreApiPath}/AccountManager/woc-modified-by`;

        if (accountManagerId) {
            url += `/${accountManagerId}`;
        }

        return this._httpClient.get<OptionModel[]>(url);
    }

    lookupWocAccountManagers(): Observable<OptionModel[]> {
        const url = `${this._coreApiPath}/AccountManager/woc-am`;
        return this._httpClient.get<OptionModel[]>(url);
    }

    downloadWocFile(fileId: number): Observable<Blob> {
        const url = `${this._coreApiPath}/woc-queue/${fileId}/woc-download`;
        return this._httpClient.get<any>(url, { responseType: 'blob' as 'json' }).pipe(
            map((response) => {
                const dataType = response.type;

                const binaryData = [];
                binaryData.push(response);

                return new Blob(binaryData, { type: dataType });
            })
        );
    }

    uploadWocFile(wud: WocUploadData): Observable<boolean> {
        const url = `${this._coreApiPath}/woc-queue/woc-upload`;
        const data = new FormData();
        data.append('ContractId', String(wud.contractId));
        data.append('StatusId', String(wud.statusId));
        data.append('ActionId', String(wud.actionId));
        data.append('UserInfoId', String(wud.userInfoId));
        data.append('WocNote', String(wud.wocNote));
        data.append('UserId', String(wud.userId));
        data.append('File', wud.file);
        data.append('FacilityId', String(wud.facilityId));

        return this._httpClient.post<boolean>(url, data);
    }

    changeWocStatus(statusChange: WocStatusChange): Observable<boolean> {
        const url = `${this._coreApiPath}/woc-queue/change-status`;

        return this._httpClient.put<boolean>(url, statusChange);
    }

    getPlacedCandidatesBulk(jobId: number, args: FetchArgs): Observable<PagingToken<CandidateBulkMovedData[]>> {
        const url = `${this._coreApiPath}/accountmanager/jobs/${jobId}/management`;
        return this.getGridPaged(args?.pageSize, args?.skip, args?.filter, args?.sort, url);
    }

    getMatchedCandidatesBulkV2(jobId: number, args: FetchArgs): Observable<PagingToken<CandidateBulkMovedData[]>> {
        const url = `${this._coreApiPath}/accountmanager/wfdjobs/${jobId}/management`;
        return this.getGridPaged(args?.pageSize, args?.skip, args?.filter, args?.sort, url);
    }

    getMatchedCandidatesBulk(args: FetchArgs): Observable<PagingToken<MatchedCandidateData[]>> {
        const url = `${this._coreApiPath}/accountmanager/jobs/additional-candidates`;
        return this._httpClient.post<PagingToken<MatchedCandidateData[]>>(url, args);
    }

    moveCandidatesBulk(currentJobId: number, data: CandidateToMoveData): Observable<void> {
        return this._httpClient.post<void>(
            `${this._coreApiPath}/accountmanager/jobs/${currentJobId}/management/move`,
            data
        );
    }

    moveContractedCandidatesFromCurrentJobsToTargetJob(data: ContractedCandidateToMoveToJobDto): Observable<void> {
        return this._httpClient.post<void>(
            `${this._coreApiPath}/accountmanager/jobs/${data.targetJobId}/management/move-to`,
            data
        );
    }

    getNewJobsBulk_WFD(
        facilityId: number,
        currentJobId: number,
        args: FetchArgs
    ): Observable<PagingToken<NewJobsBulkMoved[]>> {
        const url = `${this._coreApiPath}/accountmanager/wfdjobs/management/move/jobs-selection?facilityId=${facilityId}&currentJobId=${currentJobId}`;
        return this.postGridPaged(args, url);
    }

    placeCurrentTravelers(
        jobId: number,
        candidateIds: number[],
        sourceFacilityId: number,
        targetFacilityId: number
    ): Observable<CurrentTravelersPlacedInWFDJobDto> {
        const url = `${this._coreApiPath}/accountmanager/jobs/management/place-current-travelers`;
        return this._httpClient.post<CurrentTravelersPlacedInWFDJobDto>(url, {
            targetJobId: jobId,
            candidateIds,
            sourceFacilityId,
            targetFacilityId
        });
    }

    postSubmittalNoteReply(submittalId: number, replyParams: ReplyToSubmittalNote) {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submittalId}/client-submittal-note`;
        return this._httpClient.post<Response>(url, replyParams);
    }

    private static generateUrl(args: FetchArgs, url: string) {
        if (args.filter) {
            for (const filterName in args.filter) {
                const val = encodeURIComponent(args.filter[filterName]);

                if (Array.isArray(val)) {
                    url = appendQuery(url, arrayToQuery(`filter.${filterName}`, val as any[]));
                } else if (val) {
                    url = appendQuery(url, `filter.${filterName}=${val}`);
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(
                url,
                `sort.field=${args.sort.field}&sort.type=${args.sort.dir === 'asc' ? 'asc' : 'desc'}`
            );
        }

        url = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return url;
    }

    private getGridPaged<T>(
        pageSize: number,
        skip: number,
        filter: object,
        sort: object,
        url: string
    ): Observable<PagingToken<T[]>> {
        return this._httpClient
            .post<PagingToken<T[]>>(url, { pageSize, skip, filter, sort })
            .pipe(map((response) => response));
    }

    private postGridPaged<T>(args: FetchArgs, url: string): Observable<PagingToken<T[]>> {
        return this._httpClient.post<PagingToken<T[]>>(url, args);
    }

    private getQueryString(filter: CompositeFilterDescriptor) {
        let queryString = '';
        for (const prop in filter) {
            const val = filter[prop];
            if (Array.isArray(val)) {
                queryString = appendQuery(queryString, arrayToQuery(prop, val));
            } else if (val instanceof Date) {
                queryString = appendQuery(queryString, `${prop}=${this.formatDate(val)}`);
            } else if (val !== null && val !== undefined) {
                queryString = appendQuery(queryString, `${prop}=${val}`);
            }
        }

        return queryString;
    }

    private formatDate(date: Date) {
        const pad = (amt) => {
            if (amt < 10) {
                return '0' + amt;
            }

            return amt;
        };

        return (
            date.getFullYear() +
            '-' +
            pad(date.getMonth() + 1) +
            '-' +
            pad(date.getDate()) +
            'T' +
            pad(date.getHours()) +
            ':' +
            pad(date.getMinutes()) +
            ':' +
            pad(date.getSeconds()) +
            '.' +
            (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5) +
            'Z'
        );
    }

    bulkUpdateJobs(data): Observable<BulkUpdateJobsResult[]> {
        return this._httpClient.put<BulkUpdateJobsResult[]>(`${this._coreApiPath}/AccountManagerJobs/bulk`, data);
    }

    bulkRemoveJobs(jobsIds: number[], closeReasonId: number): Observable<Response> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: {
                jobsIds,
                closeReasonId
            }
        };
        return this._httpClient.delete<Response>(`${this._coreApiPath}/AccountManagerJobs`, options);
    }

    reassignSubmittals(amSubmittalsReassign: AmSubmittalsReassign): Observable<boolean> {
        return this._httpClient.post<boolean>(
            `${this._coreApiPath}/AccountManager/submittals/reassign`,
            amSubmittalsReassign
        );
    }

    getGridLookups(): Observable<JobGridLookups> {
        const url = `${this._coreApiPath}/Jobs/Lookups`;
        return this._httpClient.get<JobGridLookups>(url);
    }

    createOrUpdateApplicationConfiguration(
        config: SelfApplyApplicationConfigurationModel
    ): Observable<SelfApplyApplicationConfigurationModel> {
        const url = `${this._coreApiPath}/SelfApplyJob/application-configuration`;
        return this._httpClient.post<SelfApplyApplicationConfigurationModel>(url, config);
    }

    linkSampleMarginToJobs(linkDetails: SampleMarginJobsLinkModel): Observable<SampleMarginJobsLinkModel> {
        const url = `${this._coreApiPath}/AccountManager/jobs/management/sample-margin-jobs-link`;
        return this._httpClient.post<SampleMarginJobsLinkModel>(url, linkDetails);
    }

    matchJobs(candidateId: number, jobIds: number[]): Observable<boolean> {
        const body = jobIds;

        return this._httpClient
            .post<boolean>(`${this._coreApiPath}/candidate/${candidateId}/job-matches-wfd`, body)
            .pipe(map((response) => response));
    }

    getCandidateEmails(count: number, email: string, facilityId: number): Observable<ClinicianJobMatch[]> {
        return this._httpClient
            .get<
                ClinicianJobMatch[]
            >(`${this._coreApiPath}/candidate/job-match-check-email-wfd?email=${email}&facilityId=${facilityId}&count=${count}`)
            .pipe(map((response) => response.sort((first, second) => (first.email < second.email ? -1 : 1))));
    }

    getCandidateNames(count: number, fullName: string, facilityId: number): Observable<ClinicianJobMatch[]> {
        return this._httpClient
            .get<
                ClinicianJobMatch[]
            >(`${this._coreApiPath}/candidate/job-match-check-name-wfd?fullName=${fullName}&facilityId=${facilityId}&count=${count}`)
            .pipe(
                map((response: ClinicianJobMatch[]) =>
                    response
                        .map((clinician) => {
                            clinician['name'] = `${clinician.firstName} ${clinician.lastName}`;
                            return clinician;
                        })
                        .sort((first, second) => (first['name'].toLowerCase() < second['name'].toLowerCase() ? -1 : 1))
                )
            );
    }

    editOverbookOkToTravelPercent(jobs: number[], percent: number): Observable<boolean> {
        const url = `${this._coreApiPath}/AccountManager/jobs/management/overbook-ok-to-travel-percent/${percent}`;
        return this._httpClient.post<boolean>(url, jobs);
    }

    createExtension(data: JobExtensionRequest): Observable<JobExtension> {
        const url = `${this._coreApiPath}/account-manager-candidates/extensions`;
        return this._httpClient.post<JobExtension>(url, data);
    }

    getJobFulfillmentTypes(): Observable<OptionModel[]> {
        const url = `${this._coreApiPath}/accountManager/jobFulfillmentTypes`;
        return this._httpClient.get<OptionModel[]>(url);
    }

    updateKeepRequested(submittalId: number, keep: boolean) {
        const url = `${this._coreApiPath}/account-manager/submittals-pending-removal/${submittalId}/keep-request`;
        return this._httpClient.put(url, keep);
    }

    getSubmittalPreferences(submittalId: number, submittalType: SubmittalTypeEnum): Observable<SubmittalPreferences> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submittalId}/preferences?submittalType=${submittalType}`;
        return this._httpClient.get<SubmittalPreferences>(url);
    }

    updateSubmittalPreferences(submittalId: number, preferences: SubmittalPreferences): Observable<any> {
        const url = `${this._coreApiPath}/accountmanager/submittals/${submittalId}/preferences`;
        return this._httpClient.put<SubmittalPreferences>(url, preferences);
    }

    getJobTitle(jobId: number): Observable<string> {
        const url = `${this._coreApiPath}/accountmanager/jobs/${jobId}`;
        return this._httpClient.get(url).pipe(map((result: any) => result.jobTitle as string));
    }
}
